var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useEffect, useMemo, useState } from 'react';
import { useLocale } from '../../../localization';
import { useFormConfig } from './useFormConfig';
import { ValidationValueType } from './Validator/Validator';
import useFormPersistence from './Persist/createFormPersist';
// TODO: Make field.touched reactive
// TODO: Make rest reactive
export var useForm = function (config, options) {
    var _a;
    if (options === void 0) { options = {}; }
    var locale = useLocale();
    var formConfig = useFormConfig(config, options);
    var formPersist = useFormPersistence(formConfig.id);
    var _b = useState(formConfig.keys.reduce(function (prev, current) {
        var _a;
        return __assign(__assign({}, prev), (_a = {}, _a[current] = [], _a));
    }, {})), fieldListeners = _b[0], setFieldListeners = _b[1];
    var _c = useState(formConfig.initialValues), values = _c[0], setValues = _c[1];
    var _d = useState({}), errors = _d[0], setErrors = _d[1];
    // const [fieldData, setFieldData] = createStore<FieldData<T>>(
    //     formConfig().keys.reduce((prev, current: keyof T) => ({ ...prev, [current]: {
    //             showErrors: false
    //         }}), {} as FieldData<T>)
    // );
    // TODO: Merge into another signal
    var _e = useState({}), responseErrors = _e[0], setResponseErrors = _e[1];
    var _f = useState(false), submitting = _f[0], setSubmitting = _f[1];
    var _g = useState((_a = options.showErrors) !== null && _a !== void 0 ? _a : false), showErrors = _g[0], setShowErrors = _g[1];
    useEffect(function () {
        validateForm();
    }, [formConfig.fields]);
    useEffect(function () {
        formPersist.persist(formConfig, values);
    }, [values]);
    // TODO: Move
    var valueIsEmpty = function (value) {
        return value === undefined || value === null || value === '';
    };
    var validateField = function (id) {
        var field = formConfig.fields[id];
        var value = values[id];
        return field.validators
            .reduce(function (prev, current) { return __spreadArray(__spreadArray([], prev, true), current.execute(id, value, values), true); }, [])
            .filter(function (r) { return r.message; })
            .map(function (r) { return r.message; });
    };
    var validateForm = function () {
        var config = formConfig;
        var formValues = values;
        var backendResponseErrors = responseErrors;
        var valid = true;
        var errorsToSet = {};
        config.keys.forEach(function (key) {
            var _a;
            var field = config.fields[key];
            var isEmpty = valueIsEmpty(formValues[key]);
            if (!field.required && isEmpty)
                return;
            var errors = isEmpty ? [] : validateField(key);
            if (field.required && isEmpty)
                errors.push({ key: 'validation.required', values: [{ type: ValidationValueType.TRANSLATION, value: key }] });
            (_a = backendResponseErrors[key]) === null || _a === void 0 ? void 0 : _a.forEach(function (e) {
                if (e.fieldValue === formValues[key])
                    errors.push(e);
            });
            if (errors.length)
                valid = false;
            if (field.touched && field.showErrors)
                errorsToSet[key] = errors.map(function (e) { return locale.__.apply(locale, __spreadArray([e.key], e.values.map(function (v) { return v.type === ValidationValueType.VALUE ? v.value : locale.__(v.value); }), false)); });
        });
        setErrors(function () { return (__assign({}, errorsToSet)); });
        return valid;
    };
    var isValid = useMemo(function () { return validateForm(); }, [values]);
    var isDirty = function (keys, except) {
        if (keys === void 0) { keys = []; }
        if (except === void 0) { except = []; }
        return (keys.length ? keys : formConfig.keys)
            .filter(function (k) { return !except.includes(k); })
            .findIndex(function (key) { return values[key] !== formConfig.fields[key].initialValue; }) !== -1;
    };
    var setFormValues = function (values) {
        var keys = Object.keys(values).filter(function (k) { return formConfig.keys.includes(k); });
        formConfig.setTouched(keys, true);
        setValues(function (prev) { return (__assign(__assign({}, prev), values)); });
        var executedListeners = [];
        keys.forEach(function (key) {
            var listeners = fieldListeners[key];
            listeners.forEach(function (listener) {
                if (executedListeners.includes(listener))
                    return;
                listener(values);
                executedListeners.push(listener);
            });
        });
    };
    var onFormChange = function (_a) {
        var _b;
        var id = _a.id, value = _a.value;
        setFormValues((_b = {}, _b[id] = value, _b));
    };
    var watch = function (keys, listener) {
        setFieldListeners(function (fieldListeners) {
            keys.forEach(function (key) {
                fieldListeners[key].push(listener);
            });
            return __assign({}, fieldListeners);
        });
    };
    var watchExcept = function (keys, listener) {
        setFieldListeners(function (fieldListeners) {
            formConfig.keys.filter(function (k) { return !keys.includes(k); }).forEach(function (key) {
                fieldListeners[key].push(listener);
            });
            return __assign({}, fieldListeners);
        });
    };
    var finalizeForm = function () {
        formConfig.setTouchedAll(true);
        formConfig.setShowErrors(formConfig.keys, true);
        return validateForm();
    };
    var resetForm = function (keys, except) {
        if (keys === void 0) { keys = []; }
        if (except === void 0) { except = []; }
        var keysToReset = (keys.length ? keys : formConfig.keys).filter(function (k) { return !except.includes(k); });
        formConfig.setTouched(keysToReset, false);
        formConfig.setShowErrors(formConfig.keys, false);
        var valuesToSet = keysToReset.reduce(function (prev, current) {
            var _a;
            return __assign(__assign({}, prev), (_a = {}, _a[current] = formConfig.initialValues[current], _a));
        }, {});
        setValues(function (prev) { return (__assign(__assign({}, prev), valuesToSet)); });
    };
    var submit = function (callback, callbackConfig) {
        if (callbackConfig === void 0) { callbackConfig = {}; }
        if (!finalizeForm())
            return;
        var formValues = values;
        setSubmitting(true);
        callback(formValues).then(function (v) {
            var _a;
            (_a = callbackConfig.onSuccess) === null || _a === void 0 ? void 0 : _a.call(callbackConfig, v);
        }).catch(function (e) {
            var _a;
            // const formErrors = e.form();
            // const errors: ResponseErrors<T> = {};
            //
            // formErrors.forEach(e => {
            //     const field = String((options.errorResponseMap && options.errorResponseMap.hasOwnProperty(e.field))
            //         ? options.errorResponseMap[e.field]
            //         : e.field
            //     ) as keyof T;
            //
            //     if (!formConfig().keys.includes(field))
            //         return;
            //
            //     errors[field] = e.errors.map(errorCode => ({
            //         key: `validation.${errorCode}`,
            //         fieldValue: formValues[field],
            //         values: [{ value: field, type: ValidationValueType.TRANSLATION }]
            //     }));
            // });
            //
            // setResponseErrors(errors);
            //
            (_a = callbackConfig.onError) === null || _a === void 0 ? void 0 : _a.call(callbackConfig, e);
        }).finally(function () {
            var _a;
            setSubmitting(false);
            (_a = callbackConfig.onFinally) === null || _a === void 0 ? void 0 : _a.call(callbackConfig);
        });
    };
    var accessor = {
        values: values,
        errors: errors,
        config: formConfig,
        finalize: finalizeForm,
        showErrors: showErrors,
        submit: submit,
        submitting: submitting,
        isValid: isValid,
        isDirty: isDirty,
        set: setFormValues,
        reset: resetForm,
        watch: watch,
        watchExcept: watchExcept
    };
    return [accessor, onFormChange];
};
export default useForm;
